// #ux-redesign-start: Select primitive (Paso 2)
// Wrapper sobre react-select 3 con label + helper + error consistente con Input.
// Aplica estilos del DS via classNamePrefix + theme override.
//
// PORTABILIDAD: trasplantable a Web-Patients sin cambios.
//
// USO:
//   <Select
//     label="Sprache"
//     options={[{ value: 'de', label: 'Deutsch' }, { value: 'en', label: 'English' }]}
//     value={selected}
//     onChange={setSelected}
//   />
//
//   <Select async loadOptions={fetchOptions} label="Patient suchen" />
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import PropTypes from 'prop-types';
import ReactSelect from 'react-select';
import AsyncSelect from 'react-select/async';

let selectIdCounter = 0;
const nextId = () => `ds-select-${++selectIdCounter}`;

const Select = React.forwardRef(({
  id,
  name,
  label,
  helper,
  error,
  hint,
  size = 'md',
  disabled = false,
  required = false,
  async: isAsync = false,
  className = '',
  ...rest
}, ref) => {
  const fieldId = React.useMemo(() => id || nextId(), [id]);
  const helperId = helper || error ? `${fieldId}-helper` : undefined;
  const hasError = Boolean(error);

  const wrapperClasses = [
    'ds-field',
    'ds-field--select',
    `ds-field--${size}`,
    hasError ? 'ds-field--error' : '',
    disabled ? 'ds-field--disabled' : '',
    className,
  ].filter(Boolean).join(' ');

  const Component = isAsync ? AsyncSelect : ReactSelect;

  return (
    <div className={wrapperClasses}>
      {label && (
        <label htmlFor={fieldId} className="ds-field__label">
          {label}
          {required && <span className="ds-field__required" aria-hidden="true"> *</span>}
          {hint && <span className="ds-field__hint">{hint}</span>}
        </label>
      )}
      <Component
        ref={ref}
        inputId={fieldId}
        name={name}
        isDisabled={disabled}
        classNamePrefix="ds-select"
        className="ds-select-container"
        aria-invalid={hasError || undefined}
        aria-describedby={helperId}
        {...rest}
      />
      {(error || helper) && (
        <div id={helperId} className={`ds-field__helper ${hasError ? 'ds-field__helper--error' : ''}`}>
          {error || helper}
        </div>
      )}
    </div>
  );
});

Select.displayName = 'Select';

Select.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.node,
  helper: PropTypes.node,
  error: PropTypes.node,
  hint: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  async: PropTypes.bool,
  className: PropTypes.string,
};

export default Select;
// #ux-redesign-end
